import { ReactNode } from "react";
import { Box, styled, useMediaQuery } from "@mui/material";
import { tokens } from "@styles/tokens";
import authBg from "@assets/images/auth-bg.webp";
import { AUTH_CARD_WIDTH, AUTH_SPLIT_WIDTH } from "./authPrimitives";
import { AuthBrandPanel } from "./AuthBrandPanel";
import { AuthFooter } from "./AuthFooter";
import { ThemeToggle } from "./ThemeToggle";
import { Wordmark } from "./Wordmark";

interface AuthLayoutProps {
  heading: ReactNode;
  subtitle: ReactNode;
  children: ReactNode;
  /** Mobile-only: hero/header band height — shrink it for taller forms. */
  headerHeight?: string;
}

/**
 * Shared auth shell. Below {@link AUTH_SPLIT_WIDTH} it's a mobile stack (top
 * hero + centered card); at/above it, a desktop split-screen (brand panel left,
 * form right). Both consume the same heading/subtitle and form (children).
 */
export function AuthLayout({
  heading,
  subtitle,
  children,
  headerHeight = "30vh",
}: AuthLayoutProps) {
  const isDesktop = useMediaQuery(`(min-width:${AUTH_SPLIT_WIDTH}px)`, {
    noSsr: true,
  });

  if (isDesktop) {
    return (
      <DesktopScreen>
        <AuthBrandPanel />
        <FormPanel>
          <ToggleSlot>
            <ThemeToggle />
          </ToggleSlot>
          <FormColumn>
            <DesktopHeader>
              <DesktopHeading>{heading}</DesktopHeading>
              <DesktopSubtitle>{subtitle}</DesktopSubtitle>
            </DesktopHeader>
            {children}
            <AuthFooter />
          </FormColumn>
        </FormPanel>
      </DesktopScreen>
    );
  }

  return (
    <Screen>
      <HeroImage src={authBg} alt="" />
      <Content>
        <Header $height={headerHeight}>
          <Wordmark size={44} />
          <Heading>{heading}</Heading>
          <Subtitle>{subtitle}</Subtitle>
        </Header>
        {children}
      </Content>
    </Screen>
  );
}

/* ---------------------------------------------------------------- desktop -- */

const DesktopScreen = styled("div")(({ theme }) => ({
  display: "flex",
  minHeight: "100dvh",
  width: "100%",
  backgroundColor: theme.skuey.surface,
}));

const FormPanel = styled("div")(({ theme }) => ({
  position: "relative",
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  overflowY: "auto",
  backgroundColor: theme.skuey.surface,
}));

const ToggleSlot = styled("div")({
  position: "absolute",
  top: tokens.space(5),
  right: tokens.space(5),
  zIndex: 2,
});

const FormColumn = styled("div")({
  width: "100%",
  maxWidth: AUTH_CARD_WIDTH,
  margin: "auto",
  display: "flex",
  flexDirection: "column",
  gap: tokens.space(5),
  padding: `${tokens.space(14)}px ${tokens.space(6)}px ${tokens.space(8)}px`,
});

const DesktopHeader = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: tokens.space(2),
  textAlign: "center",
});

const DesktopHeading = styled("span")(({ theme }) => ({
  fontFamily: tokens.font.family,
  fontWeight: tokens.font.weight.semibold,
  fontSize: tokens.font.size.h2[0],
  lineHeight: `${tokens.font.size.h2[1]}px`,
  color: theme.skuey.ink,
}));

const DesktopSubtitle = styled("p")(({ theme }) => ({
  margin: 0,
  fontFamily: tokens.font.family,
  fontSize: tokens.font.size.small[0],
  color: theme.skuey.muted,
}));

/* ----------------------------------------------------------------- mobile -- */

// Full-height page: hero photo pinned to the top, content column centered over it.
const Screen = styled("div")(({ theme }) => ({
  position: "relative",
  minHeight: "100dvh",
  width: "100%",
  backgroundColor: theme.skuey.surfaceAlt,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
}));

// Content column sitting above the hero (z-index) — brand, form, trust badges.
// A definite width (card width + gutters) keeps every auth page the same size.
const Content = styled("div")({
  zIndex: 10,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  width: "100%",
  maxWidth: AUTH_CARD_WIDTH + tokens.space(4) * 2,
  padding: tokens.space(4),
});

// Hero photo as a real <img> (better LCP/responsiveness than a CSS background).
// The fade is a mask: two gradients fade the image's own pixels to transparent
// — vertical (out at the bottom) intersected with horizontal (out on the left).
const heroMask = `
  linear-gradient(180deg,
  rgba(0,0,0,0.95) 0%,
  rgba(0,0,0,0.7) 45%,
  rgba(0,0,0,0.15) 75%,
  rgba(0,0,0,0) 88%,
  rgba(0,0,0,0) 100%),
  linear-gradient(90deg,
  rgba(0,0,0,0) 0%,
  rgba(0,0,0,0) 45%,
  rgba(0,0,0,0.85) 100%)`;

const HeroImage = styled("img")({
  width: "100%",
  height: "50vh",
  display: "block",
  objectFit: "cover",
  objectPosition: "center bottom",
  maskImage: heroMask,
  WebkitMaskImage: heroMask,
  maskComposite: "intersect",
  WebkitMaskComposite: "source-in",
  position: "absolute",
  top: 0,
});

const Header = styled(Box, {
  shouldForwardProp: (p) => p !== "$height",
})<{ $height: string }>(({ $height }) => ({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: tokens.space(3),
  justifyContent: "flex-end",
  padding: tokens.space(6),
  height: $height,
}));

const Heading = styled("span")(({ theme }) => ({
  margin: 0,
  fontFamily: tokens.font.family,
  fontWeight: tokens.font.weight.medium,
  fontSize: "24px",
  lineHeight: 1.15,
  letterSpacing: "1px",
  color: theme.skuey.ink,
}));

const Subtitle = styled("p")(({ theme }) => ({
  margin: 0,
  fontFamily: tokens.font.family,
  fontSize: tokens.font.size.small[0],
  color: theme.skuey.muted,
}));
